import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STORAGE_KEY = 'abh_readiness_collapsed';

const ABHSystemReadiness = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await axios.get(`${API}/abh/system-readiness`);
      setData(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Unable to load readiness');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(STORAGE_KEY, String(next)); } catch { /* noop */ }
  };

  if (loading) {
    return (
      <div
        data-testid="abh-system-readiness"
        className="bg-slate-900/50 border border-slate-800 rounded-xl p-4"
      >
        <p className="text-slate-500 text-sm">Loading readiness…</p>
      </div>
    );
  }

  const layers = data?.layers || [];
  const readyCount = data?.ready_count ?? 0;
  const total = data?.total ?? 0;
  const allReady = data?.all_ready;

  return (
    <div
      data-testid="abh-system-readiness"
      className="bg-slate-900/60 border border-slate-800 rounded-xl p-5"
    >
      <button
        data-testid="abh-readiness-toggle"
        onClick={toggleCollapsed}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div className="flex items-center gap-3">
          <span
            className={`inline-block w-2.5 h-2.5 rounded-full ${
              allReady ? 'bg-emerald-400' : 'bg-amber-400'
            }`}
          />
          <h3 className="text-white font-semibold text-base">ABH System Readiness</h3>
          <span
            data-testid="abh-readiness-count"
            className={`text-xs px-2 py-0.5 rounded ${
              allReady
                ? 'bg-emerald-500/20 text-emerald-300'
                : 'bg-amber-500/20 text-amber-300'
            }`}
          >
            {readyCount} / {total} ready
          </span>
        </div>
        <span className="text-xs text-slate-400">
          {collapsed ? '▸ expand' : '▾ collapse'}
        </span>
      </button>

      {err && (
        <p className="text-sm text-red-400 mt-3">{err}</p>
      )}

      {!collapsed && !err && (
        <ul className="mt-4 space-y-2">
          {layers.map((l) => (
            <li
              key={l.name}
              data-testid={`abh-readiness-layer-${l.name.toLowerCase().replace(/\s+/g, '-')}`}
              className="flex items-start gap-3 bg-slate-800/40 rounded-lg px-3 py-2.5"
            >
              <span
                className={`mt-1.5 inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                  l.ready ? 'bg-emerald-400' : 'bg-amber-400'
                }`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm text-white font-medium">{l.name}</p>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                      l.ready
                        ? 'bg-emerald-500/20 text-emerald-300'
                        : 'bg-amber-500/20 text-amber-300'
                    }`}
                  >
                    {l.status === 'DATA_READY' ? 'DATA-READY' : 'AWAITING-SIGNAL'}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">{l.message}</p>
                {l.activation_note && (
                  <p className="text-xs text-slate-500 italic mt-1">
                    → {l.activation_note}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ABHSystemReadiness;
